import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Case, CaseProfileAnswers } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useCaseAccess } from '../../hooks/useCaseAccess';
import {
  CASE_PROFILE_FIELDS,
  buildDescriptionFromProfile,
  countAnsweredProfileFields,
  profileAnswersForCase,
  trimProfileAnswers,
} from '../../utils/caseProfile';
import { t } from '../../utils/translations';

interface CaseProfileDialogProps {
  open: boolean;
  caseItem: Case | null;
  onClose: () => void;
  onSaved?: (caseId: string) => void;
}

/** Matches the notifications sheet, so every sheet in the app moves alike. */
const SHEET_ANIMATION_MS = 280;

const CaseProfileDialog: React.FC<CaseProfileDialogProps> = ({
  open,
  caseItem,
  onClose,
  onSaved,
}) => {
  const { currentUser } = useAuth();
  const access = useCaseAccess();
  const [answers, setAnswers] = useState<CaseProfileAnswers>(() =>
    profileAnswersForCase(caseItem)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(open);

  /**
   * Callers close by clearing the case, so `caseItem` goes null at the same
   * moment `open` does. Holding the last one keeps content on screen for the
   * slide-out instead of blanking the sheet mid-animation.
   */
  const [displayCase, setDisplayCase] = useState<Case | null>(caseItem);
  useEffect(() => {
    if (caseItem) setDisplayCase(caseItem);
  }, [caseItem]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Two frames: the first paints the sheet off-screen, the second animates.
      const id = window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => setVisible(true));
      });
      return () => window.cancelAnimationFrame(id);
    }

    setVisible(false);
    const timeout = window.setTimeout(() => setMounted(false), SHEET_ANIMATION_MS);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setAnswers(profileAnswersForCase(caseItem));
    setError(null);
  }, [open, caseItem]);

  const readOnly = displayCase ? !access.canEditCase(displayCase.id) : true;
  const answered = useMemo(() => countAnsweredProfileFields(answers), [answers]);

  const handleSave = async () => {
    if (!displayCase || saving) return;
    const trimmed = trimProfileAnswers(answers);

    if (!trimmed.whyCame) {
      setError(t.caseProfile.requiredMissing);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateDoc(doc(db, 'cases', displayCase.id), {
        profile: {
          ...trimmed,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.id || '',
          updatedByName: currentUser?.fullName || '',
        },
        // Kept in sync so search, the card preview and the timeline keep working.
        description: buildDescriptionFromProfile(trimmed),
        updatedAt: serverTimestamp(),
      });
      onSaved?.(displayCase.id);
      onClose();
    } catch (err) {
      console.error('Case profile save failed:', err);
      setError(t.caseProfile.saveError);
    } finally {
      setSaving(false);
    }
  };

  if (!mounted || !displayCase) return null;

  return (
    <div className="fixed inset-0 z-[1400]">
      <div
        className={`absolute inset-0 bg-slate-900/40 transition-opacity ease-out ${
          visible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        style={{ transitionDuration: `${SHEET_ANIMATION_MS}ms` }}
        aria-hidden="true"
        onClick={saving ? undefined : onClose}
      />

      <div className="absolute inset-0 flex items-stretch justify-center sm:items-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="case-profile-title"
          className={`relative flex h-full w-full flex-col bg-white shadow-lg transition-transform ease-out sm:h-auto sm:max-h-[92vh] sm:max-w-2xl sm:rounded-2xl ${
            visible ? 'translate-y-0' : 'translate-y-full'
          }`}
          style={{
            transitionDuration: `${SHEET_ANIMATION_MS}ms`,
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5">
            <div className="min-w-0">
              <h2
                id="case-profile-title"
                className="text-base font-semibold text-slate-900"
              >
                {t.caseProfile.title}
              </h2>
              <p className="mt-0.5 truncate text-sm text-slate-500">
                {displayCase.counseledName || displayCase.title}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
              aria-label={t.common.close}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="shrink-0 border-b border-slate-100 bg-slate-50/70 px-4 py-2.5 sm:px-5">
            <p className="text-xs text-slate-600">{t.caseProfile.subtitle}</p>
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all"
                  style={{
                    width: `${(answered / CASE_PROFILE_FIELDS.length) * 100}%`,
                  }}
                />
              </div>
              <span className="shrink-0 text-[11px] font-medium text-slate-500">
                {t.caseProfile.completeness
                  .replace('{{answered}}', String(answered))
                  .replace('{{total}}', String(CASE_PROFILE_FIELDS.length))}
              </span>
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4 sm:px-5">
            {CASE_PROFILE_FIELDS.map((field) => (
              <div key={field.key}>
                <label
                  htmlFor={`case-profile-${field.key}`}
                  className="block text-sm font-medium text-slate-800"
                >
                  {field.label}
                  {!field.required && (
                    <span className="ml-1.5 text-xs font-normal text-slate-400">
                      ({t.caseProfile.optional})
                    </span>
                  )}
                </label>
                <p className="mt-0.5 text-xs text-slate-500">{field.helper}</p>
                <textarea
                  id={`case-profile-${field.key}`}
                  rows={field.rows}
                  value={answers[field.key]}
                  readOnly={readOnly}
                  onChange={(event) =>
                    setAnswers((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                  className="mt-1.5 w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none read-only:bg-slate-50 read-only:text-slate-600"
                />
              </div>
            ))}
          </div>

          <div className="shrink-0 border-t border-slate-200 px-4 py-3 sm:px-5">
            {error ? <p className="mb-2 text-sm text-red-600">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {readOnly ? t.common.close : t.common.cancel}
              </button>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving && <CircularProgress size={14} sx={{ color: 'white' }} />}
                  {t.common.save}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaseProfileDialog;
